import React from 'react';
import styled from 'astroturf/react';

import CommentBase from 'js/social/Comment/Comment';
import * as types from 'js/social/shared/types';
import useShowToggle from 'js/social/shared/useShowToggle';

const Container = styled.div<{ show?: boolean }>`
  display: none;

  &.show {
    display: block;
  }
`;

const Comment = styled(CommentBase)`
  padding-left: 24px;
`;

const ClickToExpand = styled.button`
  @import 'stylesheets/design_system/font';
  @include body__small;

  background: transparent;
  border: 0;
  color: $ds-gray-dark;
  display: flex;
  padding: 0;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;

type CommentProps = React.ComponentProps<typeof CommentBase>;

interface Props
  extends Omit<CommentProps, keyof types.Comment | 'parentCommentId'> {
  className?: string;
  comments: types.Comments;
  show: boolean;
  expandAll: boolean;
}

const PostComments: React.FunctionComponent<Props> = ({
  className,
  comments,
  show,
  expandAll,
  ...props
}) => {
  const hasSomeExpandedByDefault = comments.data.some(
    (comment) => comment.expandByDefault,
  );
  const renderShowMore =
    hasSomeExpandedByDefault &&
    !comments.data.every((comment) => comment.expandByDefault);

  // If none of the comments are expanded by default, we show all from the start
  const { isShown: showAll, show: expand } = useShowToggle(
    !hasSomeExpandedByDefault || expandAll,
  );

  const shouldShow = comments.data.length > 0 && (show || expandAll);
  if (!shouldShow) {
    return null;
  }

  return (
    <Container className={className} show={shouldShow}>
      {comments.data
        .filter((comment) => comment.expandByDefault || showAll || expandAll)
        .map((comment, index) => (
          <Comment
            key={`${comment.id}|${index}`}
            {...expand}
            {...comment}
            {...props}
          />
        ))}

      {renderShowMore && !(showAll || expandAll) && (
        <div style={{ marginLeft: 24 }}>
          <ClickToExpand onClick={expand}>Show more comments</ClickToExpand>
        </div>
      )}
    </Container>
  );
};

export default PostComments;
